/**
 * RotationPlanner — Sprint 57: Multi-Year Rotation Optimization
 *
 * 3-year grid (rows=fields, cols=years) showing current vs. optimized
 * crop rotation. Users can run the optimizer and see DB improvement delta.
 *
 * Features:
 * - Optimization button → POST /finance/rotation/optimize
 * - Collapsible constraints panel (cereal %, GLÖZ 7 toggle)
 * - Vorfrucht effect badges per cell
 * - GLÖZ 7 violation highlighting
 * - Delta bar: "+16.000 EUR über 3 Jahre"
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { getCropColor } from '../config/cropColors';

// ── Sub-components ────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-5 h-5 rounded-full border-2 border-[var(--aif-color-primary)] border-t-transparent animate-spin" />
    </div>
  );
}

function DeltaBanner({ delta, currency }) {
  const { t } = useTranslation();
  if (!delta || delta.total_3yr === undefined) return null;
  const total = delta.total_3yr;
  const perHa = delta.avg_per_ha;
  const isPositive = total > 0;
  const isZero = total === 0;

  const colorClass = isPositive
    ? 'border-green-500/30 bg-green-500/5 text-green-400'
    : isZero
    ? 'border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]'
    : 'border-red-500/30 bg-red-500/5 text-red-400';

  const sign = isPositive ? '+' : '';

  return (
    <div className={`rounded-xl border p-3 ${colorClass}`}>
      <div className="text-sm font-bold">
        {sign}{Math.round(total).toLocaleString('de-DE')} {currency || 'EUR'} {t('rotation.over3Years')}
      </div>
      <div className="text-xs mt-0.5 opacity-80">
        Ø {sign}{Math.round(perHa).toLocaleString('de-DE')} {currency || 'EUR'}/ha/Jahr
      </div>
      {isZero && (
        <div className="text-xs mt-1 opacity-70">{t('rotation.alreadyOptimal')}</div>
      )}
    </div>
  );
}

function ConstraintsPanel({ constraints, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-[var(--aif-text-secondary)] bg-[var(--aif-bg-elevated)] hover:bg-[var(--aif-bg-surface)]"
        onClick={() => setOpen(o => !o)}
        data-testid="constraints-toggle"
      >
        <span>⚙️ {t('rotation.constraintsTitle')}</span>
        <span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="p-3 space-y-3 bg-[var(--aif-bg-surface)]">
          {/* Max cereal % */}
          <div>
            <label className="block text-[10px] font-semibold text-[var(--aif-text-muted)] mb-1">
              {t('rotation.maxCerealPct')}: {constraints.max_cereal_pct}%
            </label>
            <input
              type="range" min={40} max={90} step={5}
              value={constraints.max_cereal_pct}
              onChange={e => onChange({ ...constraints, max_cereal_pct: Number(e.target.value) })}
              className="w-full h-1.5 rounded"
              data-testid="cereal-slider"
            />
            <div className="flex justify-between text-[9px] text-[var(--aif-text-muted)] mt-0.5">
              <span>40%</span><span>90%</span>
            </div>
          </div>

          {/* GLÖZ 7 toggle */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[var(--aif-text-secondary)]">
              {t('rotation.gloez7Strict')}
            </span>
            <button
              className={`relative inline-flex w-8 h-4 rounded-full transition-colors ${constraints.gloez7_strict ? 'bg-[var(--aif-color-primary)]' : 'bg-[var(--aif-bg-elevated)]'}`}
              onClick={() => onChange({ ...constraints, gloez7_strict: !constraints.gloez7_strict })}
              data-testid="gloez7-toggle"
              aria-label="GLÖZ 7 toggle"
            >
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${constraints.gloez7_strict ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * RotationGrid — main grid showing current or optimized rotation.
 * rows = fields, cols = years
 */
function RotationGrid({ plan, planningYears, title, highlightColor }) {
  const { t } = useTranslation();

  if (!plan) return null;

  // Gather fields from first year
  const year1Key = 'year_1';
  const fields = plan[year1Key] || [];
  const yearKeys = planningYears.map((_, i) => `year_${i + 1}`);

  if (!fields.length) return (
    <div className="text-xs text-[var(--aif-text-muted)] text-center py-4">{t('rotation.noFields')}</div>
  );

  return (
    <div>
      {title && (
        <div className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${highlightColor || 'text-[var(--aif-text-muted)]'}`}>
          {title}
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] text-[9px] uppercase">
              <th className="text-left p-2 pl-3">{t('rotation.field')}</th>
              {planningYears.map((yr, i) => (
                <th key={i} className="text-center p-2">{yr}</th>
              ))}
              <th className="text-right p-2 pr-3">DB/ha</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, fieldIdx) => (
              <tr
                key={field.field_id}
                className="border-t border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-elevated)]/40"
              >
                <td className="p-2 pl-3 text-[var(--aif-text-primary)] whitespace-nowrap max-w-[90px] truncate">
                  {field.field_name}
                  <div className="text-[9px] text-[var(--aif-text-muted)]">{field.area_ha} ha</div>
                </td>
                {yearKeys.map((yk, yearIdx) => {
                  const cell = (plan[yk] || [])[fieldIdx];
                  if (!cell) return <td key={yearIdx} className="text-center p-2">—</td>;
                  const color = getCropColor(cell.crop).bg;
                  const hasGloez = cell.vorfrucht_effect && cell.vorfrucht_effect.includes('-');
                  return (
                    <td
                      key={yearIdx}
                      className="text-center p-1.5"
                    >
                      <div
                        className="inline-flex flex-col items-center gap-0.5"
                        title={cell.vorfrucht_effect || cell.crop_name}
                      >
                        <div
                          className={`rounded-md px-2 py-1 text-[10px] font-semibold ${hasGloez ? 'ring-1 ring-red-500' : ''}`}
                          style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
                        >
                          {cell.crop_name?.slice(0, 7) || cell.crop}
                        </div>
                        {cell.vorfrucht_effect && (
                          <div className={`text-[8px] font-medium ${cell.vorfrucht_effect.includes('+') ? 'text-green-400' : 'text-red-400'}`}>
                            {cell.vorfrucht_effect.split(' ')[0]}
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
                {/* Avg DB/ha across years for this field */}
                <td className="text-right p-2 pr-3 text-[var(--aif-text-secondary)] font-mono text-[10px]">
                  {Math.round(
                    yearKeys.reduce((sum, yk) => sum + ((plan[yk] || [])[fieldIdx]?.db_i_per_ha || 0), 0) / yearKeys.length
                  ).toLocaleString('de-DE')} €
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] text-[9px] font-semibold">
              <td className="p-2 pl-3">{t('rotation.totalDb')}</td>
              {yearKeys.map((yk, i) => {
                const yearTotal = (plan[yk] || []).reduce((s, f) => s + (f.db_i_total || 0), 0);
                return (
                  <td key={i} className="text-center p-2">
                    {Math.round(yearTotal).toLocaleString('de-DE')} €
                  </td>
                );
              })}
              <td className="text-right p-2 pr-3 font-bold text-[var(--aif-text-primary)]">
                {Math.round(plan.total_db_3yr || 0).toLocaleString('de-DE')} €
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────

const DEFAULT_AVAILABLE_CROPS = ['wheat', 'rapeseed', 'barley', 'corn', 'sugarbeet'];

const DEFAULT_CONSTRAINTS = {
  max_cereal_pct: 66,
  gloez7_strict: true,
};

export default function RotationPlanner({ token, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [constraints, setConstraints] = useState(DEFAULT_CONSTRAINTS);
  const [availableCrops, setAvailableCrops] = useState(DEFAULT_AVAILABLE_CROPS);
  const [showCurrent, setShowCurrent] = useState(false);

  const handleOptimize = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.post('/api/v1/aif/finance/rotation/optimize', {
        years: 3,
        available_crops: availableCrops,
        constraints,
      }, { queueOnOffline: false }); // read-only POST (optimization query)
      setResult(result.data);
    } catch { /* ApiError */
      setError(t('rotation.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [token, availableCrops, constraints, t]);

  const planningYears = result?.planning_years || [
    new Date().getFullYear() + 1,
    new Date().getFullYear() + 2,
    new Date().getFullYear() + 3,
  ];

  return (
    <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)]">
        <div>
          <div className="text-sm font-bold text-[var(--aif-text-primary)]">
            🌱 {t('rotation.title')}
          </div>
          <div className="text-[10px] text-[var(--aif-text-muted)]">{t('rotation.subtitle')}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] p-1 rounded-lg"
            aria-label="close"
          >
            ✕
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Constraints */}
        <ConstraintsPanel constraints={constraints} onChange={setConstraints} />

        {/* Optimize button */}
        <button
          onClick={handleOptimize}
          disabled={loading}
          className="w-full py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
          data-testid="optimize-button"
        >
          {loading ? t('rotation.optimizing') : `🚀 ${t('rotation.optimizeButton')}`}
        </button>

        {loading && <Spinner />}

        {error && (
          <div className="text-xs text-red-400 text-center py-2">{error}</div>
        )}

        {result && !loading && (
          <>
            {/* Delta banner */}
            <DeltaBanner delta={result.delta} currency={result.currency} />

            {/* Warnings */}
            {result.warnings?.length > 0 && (
              <div className="space-y-1">
                {result.warnings.map((w, i) => (
                  <div key={i} className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[10px] text-amber-400">
                    ⚠️ {w}
                  </div>
                ))}
              </div>
            )}

            {/* Optimized rotation grid */}
            <RotationGrid
              plan={result.optimized_rotation}
              planningYears={planningYears}
              title={t('rotation.optimizedPlan')}
              highlightColor="text-green-400"
            />

            {/* Toggle current plan */}
            <button
              onClick={() => setShowCurrent(s => !s)}
              className="w-full text-[10px] text-[var(--aif-text-muted)] hover:text-[var(--aif-text-secondary)] py-1"
            >
              {showCurrent ? '▲' : '▼'} {t('rotation.showCurrent')}
            </button>

            {showCurrent && (
              <RotationGrid
                plan={result.current_rotation}
                planningYears={planningYears}
                title={t('rotation.currentPlan')}
                highlightColor="text-[var(--aif-text-muted)]"
              />
            )}

            {/* Metadata */}
            <div className="text-[9px] text-[var(--aif-text-muted)] text-center">
              {result.fields_analyzed} {t('rotation.fieldsAnalyzed')} ·{' '}
              {result.algorithm === 'brute_force' ? t('rotation.algoBrute') : t('rotation.algoGreedy')}
            </div>
          </>
        )}

        {!result && !loading && (
          <div className="text-center py-6">
            <div className="text-3xl mb-2">🌾</div>
            <div className="text-xs text-[var(--aif-text-muted)] max-w-[220px] mx-auto">
              {t('rotation.startHint')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
